import { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import {
  getDesignationsAPI,
  deleteDesignationAPI,
  toggleDesignationStatusAPI,
  bulkDeleteDesignationsAPI,
  bulkToggleDesignationStatusAPI,
  getDesignationFilterOptionsAPI,
} from "../../../api/admin/adminDesignationAPI";

export const useDesignationListManagement = () => {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    industry_ids: [],
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
    setPage(1);
  };

  const [industries, setIndustries] = useState([]);

  // Keep filters stable in a ref for the fetch callback
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch the industries list for the filter dropdown
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await getDesignationFilterOptionsAPI();
        const data = response?.data || response || {};
        setIndustries(data.industries || []);
      } catch (error) {
        console.error("Error fetching designation filter options:", error);
        setIndustries([]);
      }
    };
    fetchFilterOptions();
  }, []);

  // Core fetch function – depends only on page/sort, uses filters from ref
  const fetchDesignations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search: filtersRef.current.search || undefined,
        status: filtersRef.current.status || undefined,
        industry_ids: filtersRef.current.industry_ids || [],
        sort_by: sortConfig.key || undefined,
        sort_dir: sortConfig.key ? sortConfig.direction : undefined,
      };
      const response = await getDesignationsAPI(params);
      const responseData = response.data;
      setDesignations(responseData.data || []);
      setTotalPages(responseData.last_page || 1);
      setTotalRecords(responseData.total || 0);
    } catch (error) {
      console.error("Error fetching designations:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load designations",
        text: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  }, [page, sortConfig]);

  // Trigger a fresh fetch (used by search, filters, clear, etc.)
  const triggerFetch = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // Fetch whenever page or refreshTrigger changes
  useEffect(() => {
    fetchDesignations();
  }, [page, refreshTrigger, fetchDesignations]);

  // Selection is tied to the current page/filters — reset it whenever either changes
  useEffect(() => {
    setSelectedIds([]);
  }, [page, refreshTrigger]);

  const toggleSelectDesignation = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === designations.length ? [] : designations.map((d) => d.id),
    );
  };

  // Search/filter handler: reset page to 1 and force a refetch
  const handleSearch = useCallback(() => {
    setPage(1);
    triggerFetch();
  }, [triggerFetch]);

  // Clear all filters and refetch
  const handleClear = useCallback(() => {
    setFilters({ search: "", status: "", industry_ids: [] });
    setPage(1);
    triggerFetch();
  }, [triggerFetch]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deleteDesignationAPI(id);

      if (response?.status === 500 || response?.data?.success === false) {
        Swal.fire({
          icon: "error",
          title: "Delete failed",
          text: response?.message || "This designation cannot be deleted",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Designation has been deleted.",
      });

      fetchDesignations();
    } catch (error) {
      console.error("Error deleting designation:", error);

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const action = Number(currentStatus) === 1 ? "deactivate" : "activate";
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `Do you want to ${action} this designation?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action} it!`,
    });

    if (!result.isConfirmed) return;

    try {
      await toggleDesignationStatusAPI(id);
      Swal.fire("Success!", `Designation ${action}d successfully.`, "success");
      fetchDesignations();
    } catch (error) {
      console.error("Error toggling status:", error);
      Swal.fire({
        icon: "error",
        title: "Action failed",
        text: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleBulkStatusChange = async (activate) => {
    if (selectedIds.length === 0) return;
    const actionText = activate ? "activate" : "deactivate";
    const count = selectedIds.length;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `${count} selected designation${count > 1 ? "s" : ""} will be ${actionText}d.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: activate ? "#16a34a" : "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (!result.isConfirmed) return;

    try {
      await bulkToggleDesignationStatusAPI(selectedIds, activate ? 1 : 0);
      setSelectedIds([]);
      fetchDesignations();

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Selected designations have been ${actionText}d.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to ${actionText} selected designations.`,
      });
    }
  };

  const handleBulkActivate = () => handleBulkStatusChange(true);
  const handleBulkDeactivate = () => handleBulkStatusChange(false);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `${count} selected designation${count > 1 ? "s" : ""} will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (!result.isConfirmed) return;

    try {
      await bulkDeleteDesignationsAPI(selectedIds);
      setSelectedIds([]);
      fetchDesignations();

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Selected designations have been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete selected designations.",
      });
    }
  };

  return {
    designations,
    loading,
    page,
    totalPages,
    totalRecords,
    filters,
    setFilters,
    setPage,
    handleSearch,
    handleClear,
    handleDelete,
    handleToggleStatus,
    refresh: fetchDesignations,
    industries,
    selectedIds,
    toggleSelectDesignation,
    toggleSelectAll,
    handleBulkActivate,
    handleBulkDeactivate,
    handleBulkDelete,
    sortConfig,
    handleSort,
  };
};
